import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { firebaseResetPassword } from '../lib/firebase';
import { toast } from 'sonner';
import { Building2, Mail, Lock, ArrowLeft, Building, Users, CreditCard } from 'lucide-react';

const TEAL = '#00a99d';
const DARK = '#1a1a2e';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success('Welcome back!');
      if (user.role === 'SUPER_ADMIN') navigate('/admin');
      else if (user.role === 'LANDLORD') navigate('/landlord');
      else navigate('/tenant');
    } catch (error) {
      const code = error?.code || '';
      const msg = code.includes('invalid-credential') || code.includes('wrong-password') || code.includes('user-not-found')
        ? 'Invalid email or password.'
        : code.includes('too-many-requests')
        ? 'Too many attempts. Please wait.'
        : error.response?.data?.detail || 'Login failed.';
      toast.error(msg);
    } finally { setLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail) { toast.error('Please enter your email'); return; }
    setResetLoading(true);
    try {
      await firebaseResetPassword(resetEmail);
      toast.success('Reset email sent! Check your inbox.');
      setShowForgot(false);
      setEmail(resetEmail);
    } catch (error) {
      const code = error?.code || '';
      toast.error(code.includes('user-not-found') ? 'No account found.' : 'Failed to send reset email.');
    } finally { setResetLoading(false); }
  };

  const inputStyle = {
    width: '100%', height: 48, padding: '0 16px 0 48px', borderRadius: 10,
    border: '1px solid #e5e7eb', fontSize: 15, color: DARK, background: '#fff',
    outline: 'none', transition: 'border-color 0.2s',
  };

  const btnStyle = {
    width: '100%', height: 48, borderRadius: 10, border: 'none',
    background: TEAL, color: '#fff', fontSize: 15, fontWeight: 600,
    cursor: 'pointer', transition: 'opacity 0.2s', letterSpacing: 0.5,
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', fontFamily: "'Inter', system-ui, sans-serif" }} data-testid="login-page">
      {/* Left Dark Panel */}
      <div className="hidden lg:flex" style={{
        width: '45%', background: DARK, flexDirection: 'column', justifyContent: 'center',
        padding: '48px 56px', position: 'relative',
      }}>
        <div style={{ marginBottom: 48 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 32 }}>
            <Building2 style={{ width: 32, height: 32, color: TEAL }} />
            <span style={{ fontSize: 26, fontWeight: 700, color: '#fff' }}>
              Realty <span style={{ color: TEAL }}>JAG</span>
            </span>
          </div>
          <p style={{ fontSize: 22, fontWeight: 600, color: '#fff', lineHeight: 1.4, marginBottom: 8 }}>
            Property Management Made Simple
          </p>
          <p style={{ fontSize: 15, color: '#94a3b8', lineHeight: 1.6 }}>
            Collect rent online, manage leases, track maintenance — all in one platform.
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {[
            { icon: Building, num: '500+', label: 'Properties Managed' },
            { icon: Users, num: '2,000+', label: 'Units Tracked' },
            { icon: CreditCard, num: '$5M+', label: 'Rent Collected' },
          ].map(s => (
            <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ width: 44, height: 44, borderRadius: 10, background: 'rgba(0,169,157,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <s.icon style={{ width: 20, height: 20, color: TEAL }} />
              </div>
              <div>
                <p style={{ fontSize: 20, fontWeight: 700, color: '#fff', margin: 0 }}>{s.num}</p>
                <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>{s.label}</p>
              </div>
            </div>
          ))}
        </div>
        <div style={{ position: 'absolute', bottom: 24, left: 56, right: 56 }}>
          <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#64748b' }}>
            <a href="/privacy" style={{ color: '#64748b', textDecoration: 'none' }}>Privacy Policy</a>
            <a href="/terms" style={{ color: '#64748b', textDecoration: 'none' }}>Terms of Service</a>
          </div>
          <p style={{ fontSize: 11, color: '#4b5563', margin: '8px 0 0' }}>&copy; {new Date().getFullYear()} Realty JAG LLC</p>
        </div>
      </div>

      {/* Right White Form */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32, background: '#fff' }}>
        <div style={{ width: '100%', maxWidth: 400 }}>
          {/* Mobile logo */}
          <div className="lg:hidden" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 40 }}>
            <Building2 style={{ width: 28, height: 28, color: TEAL }} />
            <span style={{ fontSize: 22, fontWeight: 700, color: DARK }}>Realty <span style={{ color: TEAL }}>JAG</span></span>
          </div>

          {showForgot ? (
            <>
              <button
                onClick={() => setShowForgot(false)}
                data-testid="back-to-login-btn"
                style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', marginBottom: 24 }}
              >
                <ArrowLeft style={{ width: 16, height: 16 }} /> Back to sign in
              </button>
              <h2 style={{ fontSize: 28, fontWeight: 700, color: DARK, marginBottom: 8 }}>Reset Password</h2>
              <p style={{ fontSize: 15, color: '#64748b', marginBottom: 32 }}>Enter your email and we'll send a reset link.</p>
              <form onSubmit={handleResetPassword}>
                <label style={{ fontSize: 14, fontWeight: 500, color: '#64748b', display: 'block', marginBottom: 8 }}>Email</label>
                <div style={{ position: 'relative', marginBottom: 24 }}>
                  <Mail style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', width: 18, height: 18, color: '#94a3b8' }} />
                  <input
                    type="email" value={resetEmail} onChange={e => setResetEmail(e.target.value)}
                    placeholder="you@example.com" required data-testid="reset-email-input"
                    style={inputStyle} onFocus={e => e.target.style.borderColor = TEAL} onBlur={e => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
                <button type="submit" disabled={resetLoading} style={{ ...btnStyle, opacity: resetLoading ? 0.7 : 1 }} data-testid="reset-password-submit-btn">
                  {resetLoading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </form>
            </>
          ) : (
            <>
              <h2 style={{ fontSize: 28, fontWeight: 700, color: DARK, marginBottom: 8 }}>Welcome Back</h2>
              <p style={{ fontSize: 15, color: '#64748b', marginBottom: 32 }}>Sign in to your account</p>
              <form onSubmit={handleSubmit}>
                <label style={{ fontSize: 14, fontWeight: 500, color: '#64748b', display: 'block', marginBottom: 8 }}>Email</label>
                <div style={{ position: 'relative', marginBottom: 20 }}>
                  <Mail style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', width: 18, height: 18, color: '#94a3b8' }} />
                  <input
                    type="email" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="you@example.com" required data-testid="login-email-input"
                    style={inputStyle} onFocus={e => e.target.style.borderColor = TEAL} onBlur={e => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <label style={{ fontSize: 14, fontWeight: 500, color: '#64748b' }}>Password</label>
                  <button type="button" onClick={() => { setShowForgot(true); setResetEmail(email); }}
                    data-testid="forgot-password-link"
                    style={{ fontSize: 13, color: TEAL, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500 }}>
                    Forgot password?
                  </button>
                </div>
                <div style={{ position: 'relative', marginBottom: 28 }}>
                  <Lock style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', width: 18, height: 18, color: '#94a3b8' }} />
                  <input
                    type="password" value={password} onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••" required data-testid="login-password-input"
                    style={inputStyle} onFocus={e => e.target.style.borderColor = TEAL} onBlur={e => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
                <button type="submit" disabled={loading} style={{ ...btnStyle, opacity: loading ? 0.7 : 1 }} data-testid="login-submit-btn">
                  {loading ? 'Signing in...' : 'Sign In'}
                </button>
              </form>
              <p style={{ marginTop: 28, textAlign: 'center', fontSize: 14, color: '#64748b' }}>
                Don't have an account?{' '}
                <Link to="/register" style={{ color: TEAL, fontWeight: 600, textDecoration: 'none' }} data-testid="register-link">
                  Create Account
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
